// Data-related constants
import { 
  Gender, 
  BloodType, 
  AppointmentStatus,
  EnumHelpers 
} from '../types/enums';

export const DATA_CONSTANTS = {
  GENDER_OPTIONS: EnumHelpers.getGenderValues(),
  BLOOD_TYPES: EnumHelpers.getBloodTypeValues(),
  APPOINTMENT_STATUSES: EnumHelpers.getAppointmentStatusValues(),
  DEV_PATIENT_SHORTCUTS: [
    {
      firstName: 'Devon',
      lastName: 'Lane',
      dateOfBirth: '1992-02-12',
      gender: Gender.FEMALE,
      phoneNumber: '(555) 410-0021',
      email: 'devon.lane@docx.dev',
      address: '12 Bay Street',
      bloodType: BloodType.A_POSITIVE,
      allergies: 'Peanuts',
      medications: 'Vitamin D',
      emergencyContactName: 'Riley Lane',
      emergencyContactRelationship: 'Sibling',
      emergencyContactPhone: '(555) 410-0022',
    },
    {
      firstName: 'Jordan',
      lastName: 'Nguyen',
      dateOfBirth: '1987-09-06',
      gender: Gender.MALE,
      phoneNumber: '(555) 410-0031',
      email: 'jordan.nguyen@docx.dev',
      address: '48 North Road',
      bloodType: BloodType.O_NEGATIVE,
      allergies: 'Latex',
      medications: 'Metformin',
      emergencyContactName: 'Avery Nguyen',
      emergencyContactRelationship: 'Partner',
      emergencyContactPhone: '(555) 410-0032',
    },
    {
      firstName: 'Avery',
      lastName: 'Patel',
      dateOfBirth: '1998-04-21',
      gender: Gender.OTHER,
      phoneNumber: '(555) 410-0041',
      email: 'avery.patel@docx.dev',
      address: '90 Market Lane',
      bloodType: BloodType.B_POSITIVE,
      allergies: 'Pollen',
      medications: 'Ibuprofen',
      emergencyContactName: 'Sam Patel',
      emergencyContactRelationship: 'Parent',
      emergencyContactPhone: '(555) 410-0042',
    },
  ],
  DEV_RANDOM: {
    FIRST_NAMES: ['Avery', 'Jordan', 'Riley', 'Kai', 'Morgan', 'Taylor'],
    LAST_NAMES: ['Reed', 'Hayes', 'Parker', 'Rivera', 'Kim', 'Singh'],
    EMAIL_DOMAINS: ['docx.dev', 'demo.docx.dev', 'mail.docx.dev'],
    PHONE_SUFFIXES: ['2101234', '2105678', '3104567', '4107890', '5102345', '6106789'],
    PHONE_PREFIX: '+1555',
    ADDRESSES: ['12 Bay Street', '48 North Road', '90 Market Lane', '77 River Ave'],
    CHIEF_COMPLAINTS: ['Persistent headache', 'Follow-up for blood pressure', 'Fatigue and dizziness'],
    SYMPTOMS: ['Headache', 'Fatigue', 'Dizziness', 'Nausea', 'Cough', 'Fever'],
    DIAGNOSES: ['Tension headache', 'Viral infection', 'Hypertension review'],
    NOTES: ['Patient advised rest and hydration.', 'Monitor symptoms and follow up in one week.'],
    LAB_TESTS: ['CBC', 'Lipid Panel', 'Blood Glucose', 'Thyroid Panel'],
    APPOINTMENT_TYPES: [
      'General Checkup',
      'Follow-up',
      'Consultation',
      'Physical Exam',
      'Lab Review',
    ],
    APPOINTMENT_DURATIONS: [15, 30, 45, 60],
    ROOMS: ['Room 201', 'Room 202', 'Room 203', 'Room 204'],
    DEFAULT_DOB: '1990-01-01',
    DEFAULT_ALLERGIES: 'Peanuts',
    DEFAULT_MEDICATIONS: 'Vitamin D',
    DEFAULT_EMERGENCY_CONTACT_NAME: 'Alex Reed',
    DEFAULT_EMERGENCY_CONTACT_RELATIONSHIP: 'Sibling',
    MEDICATIONS: [
      {
        name: 'Paracetamol',
        dosage: '500mg',
        frequency: 'Twice daily',
        timing: 'After meals',
        duration: '5 days',
      },
      {
        name: 'Ibuprofen',
        dosage: '200mg',
        frequency: 'Once daily',
        timing: 'With food',
        duration: '3 days',
      },
      {
        name: 'Cetirizine',
        dosage: '10mg',
        frequency: 'Once daily',
        timing: 'Evening',
        duration: '7 days',
      },
    ],
  },
} as const;

// Mock data for demonstration
export const MOCK_DATA = {
  PATIENTS: [
    {
      id: '1',
      firstName: 'John',
      lastName: 'Doe',
      dateOfBirth: '1985-06-15',
      gender: Gender.MALE,
      phoneNumber: '(555) 123-4567',
      email: 'john.doe@email.com',
      address: '123 Main St, City, State 12345',
      bloodType: BloodType.A_POSITIVE,
      allergies: ['Penicillin'],
      medications: ['Aspirin'],
      emergencyContact: {
        name: 'Jane Doe',
        relationship: 'Spouse',
        phoneNumber: '(555) 987-6543'
      },
      createdAt: '2024-01-15T10:00:00Z',
      updatedAt: '2024-01-15T10:00:00Z'
    },
    {
      id: '2',
      firstName: 'Alice',
      lastName: 'Smith',
      dateOfBirth: '1990-03-22',
      gender: Gender.FEMALE,
      phoneNumber: '(555) 234-5678',
      email: 'alice.smith@email.com',
      address: '456 Oak Ave, City, State 12345',
      bloodType: BloodType.A_NEGATIVE,
      allergies: ['Shellfish'],
      medications: ['Vitamin D'],
      emergencyContact: {
        name: 'Bob Smith',
        relationship: 'Brother',
        phoneNumber: '(555) 876-5432'
      },
      createdAt: '2024-01-20T11:30:00Z',
      updatedAt: '2024-01-20T11:30:00Z'
    }
  ],
  DOCTORS: [
    {
      id: '1',
      firstName: 'Dr. Sarah',
      lastName: 'Johnson',
      specialization: 'Cardiology',
      licenseNumber: 'MD12345',
      phoneNumber: '(555) 234-5678',
      email: 'sarah.johnson@hospital.com',
      department: 'Cardiology',
      experience: 8,
      education: ['MD from Harvard Medical School', 'Residency at Johns Hopkins'],
      availableHours: {
        start: '09:00',
        end: '17:00'
      },
      consultationFee: 250,
      isActive: true,
      status: 'Active',
      createdAt: '2024-01-10T10:00:00Z',
      updatedAt: '2024-01-10T10:00:00Z'
    },
    {
      id: '2',
      firstName: 'Dr. Michael',
      lastName: 'Chen',
      specialization: 'Neurology',
      licenseNumber: 'MD67890',
      phoneNumber: '(555) 345-6789',
      email: 'michael.chen@hospital.com',
      department: 'Neurology',
      experience: 12,
      education: ['MD from Stanford Medical School', 'Fellowship in Neurosurgery'],
      availableHours: {
        start: '08:00',
        end: '18:00'
      },
      consultationFee: 300,
      isActive: true,
      status: 'Active',
      createdAt: '2024-01-12T09:00:00Z',
      updatedAt: '2024-01-12T09:00:00Z'
    }
  ],

  // Appointments list for dashboard and appointments page
  APPOINTMENTS: [
    {
      id: '1',
      patientName: 'Liam Johnson',
      patientAge: 34,
      patientGender: Gender.MALE,
      date: 'November 9, 2025',
      time: '09:00 AM',
      type: 'General Checkup',
      status: AppointmentStatus.SCHEDULED,
      duration: '30 minutes',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Liam',
    },
    {
      id: '2',
      patientName: 'Emma Wilson',
      patientAge: 28,
      patientGender: Gender.FEMALE,
      date: 'November 9, 2025',
      time: '10:00 AM',
      type: 'Follow-up Visit',
      status: AppointmentStatus.SCHEDULED,
      duration: '20 minutes',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Emma',
    },
    {
      id: '3',
      patientName: 'Noah Brown',
      patientAge: 45,
      patientGender: Gender.MALE,
      date: 'November 9, 2025',
      time: '11:30 AM',
      type: 'Consultation',
      status: AppointmentStatus.SCHEDULED,
      duration: '45 minutes',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Noah',
    },
    {
      id: '4',
      patientName: 'Olivia Davis',
      patientAge: 52,
      patientGender: Gender.FEMALE,
      date: 'November 9, 2025',
      time: '02:00 PM',
      type: 'Lab Results Review',
      status: AppointmentStatus.COMPLETED,
      duration: '15 minutes',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Olivia',
    },
    {
      id: '5',
      patientName: 'James Martinez',
      patientAge: 39,
      patientGender: Gender.MALE,
      date: 'November 10, 2025',
      time: '09:30 AM',
      type: 'Physical Examination',
      status: AppointmentStatus.SCHEDULED,
      duration: '40 minutes',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=James',
    },
    {
      id: '6',
      patientName: 'Sophia Anderson',
      patientAge: 31,
      patientGender: Gender.FEMALE,
      date: 'November 10, 2025',
      time: '11:00 AM',
      type: 'Follow-up Visit',
      status: AppointmentStatus.SCHEDULED,
      duration: '25 minutes',
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sophia',
    },
  ],

  // Detailed appointment data
  APPOINTMENT_DETAILS: {
    patient: {
      id: '1',
      name: 'Liam Johnson',
      patientId: '#PT-2024-001',
      age: 34,
      gender: Gender.MALE,
      contact: '+1 (555) 123-4567',
      email: 'liam.johnson@email.com',
      bloodType: BloodType.O_POSITIVE,
      avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Liam',
    },
    appointment: {
      id: '1',
      type: 'General Checkup',
      status: AppointmentStatus.SCHEDULED,
      date: 'November 9, 2025',
      time: '09:00 AM',
      duration: '30 minutes',
      room: 'Room 201',
    },
    chiefComplaint: {
      description: 'Patient reports feeling fatigued and experiencing occasional chest discomfort during physical activity.',
      symptoms: ['Fatigue', 'Chest Discomfort', 'Shortness of Breath'],
      duration: 'Symptoms started approximately 2 weeks ago',
    },
    medicalHistory: {
      previousConditions: 'Hypertension (controlled with medication)',
      currentMedications: 'Lisinopril 10mg daily, Aspirin 81mg daily',
      allergies: 'Penicillin',
    },
    vitals: {
      heartRate: '72 bpm',
      bloodPressure: '140/90',
      temperature: '98.6°F',
      weight: '185 lbs',
      respiratoryRate: '16 /min',
      oxygenSaturation: '98%',
    },
    diagnosis: 'Patient is in good health. Blood pressure slightly elevated but within acceptable range.',
    medications: [
      {
        name: 'Lisinopril',
        dosage: '10mg',
        frequency: 'Once daily',
        timing: 'Morning',
        duration: '90 days',
      },
    ],
    labTests: ['Complete Blood Count', 'Lipid Panel', 'Hemoglobin A1C'],
    doctorNotes: 'Patient reports good adherence to current medication regimen. Advised to continue with regular exercise and balanced diet. Monitor blood pressure at home weekly.',
    followUpDate: 'January 15, 2026',
    notes: '',
    reminder: 'Follow up appointment recommended in 2 weeks to review test results.',
    previousAppointments: [
      {
        id: 'prev-1',
        type: 'Annual Physical',
        date: 'Oct 15, 2025',
      },
      {
        id: 'prev-2',
        type: 'Follow-up Visit',
        date: 'Sep 3, 2025',
      },
      {
        id: 'prev-3',
        type: 'Initial Consultation',
        date: 'Aug 12, 2025',
      },
    ],
  },

  DASHBOARD_STATS: {
    totalPatients: 145,
    totalDoctors: 12,
    activeDoctors: 10,
    appointmentsToday: 8,
  },
  
  // Mock user accounts for authentication
  USERS: [
    {
      id: '1',
      email: 'doctor@docx.com',
      password: 'demo123', // In production, this would be hashed
      name: 'Dr. Sarah Johnson',
      role: 'doctor',
      doctorId: '1',
    },
    {
      id: '2',
      email: 'admin@docx.com',
      password: 'demo123',
      name: 'Admin User',
      role: 'admin',
    },
    {
      id: '3',
      email: 'demo@docx.com',
      password: 'demo123',
      name: 'Dr. Demo',
      role: 'doctor',
    }
  ],

  // Consultation quick-add options
  COMMON_SYMPTOMS: ['Fever', 'Cough', 'Headache', 'Fatigue', 'Nausea', 'Chest Pain'],
  COMMON_TESTS: ['CBC', 'Blood Sugar', 'Lipid Profile', 'ECG', 'X-Ray', 'Urine Test'],
  COMMON_MEDICINES: ['Paracetamol', 'Ibuprofen', 'Amoxicillin', 'Azithromycin', 'Omeprazole', 'Metformin'],

  // Patient History Card Configurations (for rendering history grids)
  PATIENT_HISTORY_CONFIG: [
    { key: 'allergies', icon: 'warning', titleKey: 'KNOWN_ALLERGIES' },
    { key: 'familyHistory', icon: 'family_restroom', titleKey: 'FAMILY_HISTORY' },
    { key: 'surgeries', icon: 'surgical', titleKey: 'PAST_SURGERIES' },
    { key: 'lifestyle', icon: 'groups', titleKey: 'LIFESTYLE_HABITS' },
    { key: 'conditions', icon: 'medical_information', titleKey: 'PREVIOUS_CONDITIONS' },
  ] as const,

  // Default patient history data (mock)
  DEFAULT_PATIENT_HISTORY: {
    allergies: ['Penicillin (Rash)', 'Pollen (Seasonal)'],
    familyHistory: ['Father: Heart Disease', 'Mother: Diabetes'],
    surgeries: ['Appendectomy (2015)'],
    lifestyle: ['Non-smoker', 'Occasional alcohol use', 'Regular exercise (3x/week)'],
    conditions: ['Hypertension (Diagnosed 2020)', 'Type 2 Diabetes (Diagnosed 2018)', 'Seasonal Allergies'],
    previousTests: ['CBC (Jan 2026)', 'Lipid Panel (Dec 2025)', 'HbA1c (Nov 2025)'],
  } as const,

  // Vitals configuration (for rendering vitals display/edit)
  VITALS_CONFIG: [
    { key: 'heartRate', icon: 'favorite', labelKey: 'HEART_RATE', placeholderKey: 'HEART_RATE', shortLabel: 'HR' },
    { key: 'bloodPressure', icon: 'monitor_heart', labelKey: 'BLOOD_PRESSURE', placeholderKey: 'BLOOD_PRESSURE', shortLabel: 'BP' },
    { key: 'temperature', icon: 'thermostat', labelKey: 'TEMPERATURE', placeholderKey: 'TEMPERATURE', shortLabel: 'Temp' },
    { key: 'weight', icon: 'scale', labelKey: 'WEIGHT', placeholderKey: 'WEIGHT', shortLabel: 'Weight' },
  ] as const,

  // Extended vitals configuration (for consultation with 6 vitals)
  VITALS_CONFIG_EXTENDED: [
    { key: 'heartRate', icon: 'favorite', labelKey: 'HEART_RATE', placeholderKey: 'HEART_RATE', shortLabel: 'HR' },
    { key: 'bloodPressure', icon: 'monitor_heart', labelKey: 'BLOOD_PRESSURE', placeholderKey: 'BLOOD_PRESSURE', shortLabel: 'BP' },
    { key: 'temperature', icon: 'thermostat', labelKey: 'TEMPERATURE', placeholderKey: 'TEMPERATURE', shortLabel: 'Temp' },
    { key: 'weight', icon: 'scale', labelKey: 'WEIGHT', placeholderKey: 'WEIGHT', shortLabel: 'Weight' },
    { key: 'respiratoryRate', icon: 'air', labelKey: 'RESPIRATORY_RATE', placeholderKey: 'RESPIRATORY_RATE', shortLabel: 'RR' },
    { key: 'oxygenSaturation', icon: 'bloodtype', labelKey: 'OXYGEN_SATURATION', placeholderKey: 'OXYGEN_SATURATION', shortLabel: 'SpO₂' },
  ] as const,

  // Consultation mock prescription items
  CONSULTATION_PRESCRIPTIONS: [
    { id: '1', name: 'Metformin 500mg', details: '500mg \u2022 Twice daily \u2022 After meals \u2022 30 days' },
    { id: '2', name: 'Lisinopril 10mg', details: '10mg \u2022 Once daily \u2022 Morning \u2022 30 days' },
    { id: '3', name: 'Aspirin 81mg', details: '81mg \u2022 Once daily \u2022 After meals \u2022 30 days' },
  ] as const,

  // Consultation mock lab test items
  CONSULTATION_LAB_TESTS: [
    { id: '1', name: 'Complete Blood Count (CBC)', details: 'Fasting required \u2022 Sample: Blood \u2022 Priority: Routine' },
    { id: '2', name: 'Lipid Profile', details: 'Fasting required \u2022 Sample: Blood \u2022 Priority: Routine' },
    { id: '3', name: 'HbA1c', details: 'No fasting \u2022 Sample: Blood \u2022 Priority: Routine' },
    { id: '4', name: 'Kidney Function Test', details: 'Fasting preferred \u2022 Sample: Blood & Urine \u2022 Priority: Routine' },
  ] as const,

  // Quick-add service chips for New Invoice
  QUICK_ADD_SERVICES: [
    { name: 'Consultation Fee', price: 150 },
    { name: 'Follow-up Visit', price: 75 },
    { name: 'CBC Test', price: 50 },
    { name: 'X-Ray', price: 120 },
    { name: 'ECG', price: 80 },
    { name: 'Prescription', price: 30 },
    { name: 'Ultrasound', price: 200 },
    { name: 'Blood Sugar Test', price: 40 },
  ],

  // Settings mock profile data
  SETTINGS_PROFILE: {
    firstName: 'John',
    lastName: 'Smith',
    phoneNumber: '+1 (555) 123-4567',
    email: 'dr.smith@hospital.com',
  } as const,

  // Settings mock professional data
  SETTINGS_PROFESSIONAL: {
    specialization: 'General Practitioner',
    education: 'MD, Harvard Medical School',
    licenseNumber: 'MD-12345',
    yearsOfExperience: 15,
  } as const,

  // Settings mock session data
  SETTINGS_SESSIONS: [
    {
      device: 'MacBook Pro',
      icon: 'computer',
      location: 'San Francisco, CA \u2022 Last active: Now',
      isCurrent: true,
    },
    {
      device: 'iPhone 13',
      icon: 'phone_iphone',
      location: 'San Francisco, CA \u2022 Last active: 2 hours ago',
      isCurrent: false,
    },
  ] as const,
};

// Application-level configuration
export const APP_CONFIG = {
  APP_NAME: 'Medical Records Dashboard',
  DEFAULT_TAB: 'dashboard' as const,
  MAX_SEARCH_RESULTS: 100,
  CONSULTATION_DURATION_DEFAULT: 30, // minutes,
} as const;

export const REGISTRATION_CONFIG = {
  SPECIALIZATIONS: [
    'General Medicine',
    'Cardiology',
    'Dermatology',
    'Endocrinology',
    'Gastroenterology',
    'Neurology',
    'Obstetrics & Gynecology',
    'Oncology',
    'Ophthalmology',
    'Orthopedics',
    'Pediatrics',
    'Psychiatry',
    'Pulmonology',
    'Radiology',
    'Surgery',
    'Urology',
    'Other',
  ],
  PASSWORD_MIN_LENGTH: 6,
  NAME_MIN_LENGTH: 2,
  LICENSE_MIN_LENGTH: 2,
} as const;
